import {Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {NgxIndexedDBService} from "ngx-indexed-db";
import {AuthorityService} from "../../service/authority.service";
import * as Sentry from "@sentry/angular";

/**
 * Upload multiple own files(authorities)
 */
@Component({
  selector: 'app-dropzone',
  templateUrl: './dropzone.component.html',
  styleUrls: ['./dropzone.component.css']
})
export class DropzoneComponent implements OnInit {

  constructor(private indexDBService: NgxIndexedDBService, private authoritySevice: AuthorityService) { }

  @ViewChild("fileInput") fileInput: ElementRef;
  //POST URL for posting added files
  @Input() uploadUrl: string;
  //GET URL for getting already uploaded files
  @Input() uploadedFilesUrl: string;
  //Text and Image that are being displayed inside the Component
  @Input() dropzoneText = "";
  @Input() dropzoneImage;

  @Input() pixelHeight: number = 130;
  @Input() percentWidth: number = 70;

  @Input() authorityType: string = "authority";
  //whether or not already uploaded files should be checked
  @Input() getUploadedFiles: boolean = true;
  @Input() hasBorder: boolean = true;
  //whether newly added files are appended to the already selected files (true) or replace them (false)
  @Input() appendFiles: boolean = false;

  @Input() maxFileSize: number = 20000000;
  errorMessage: string;

  files: any = [];
  loadingAlreadyUploadedFiles: boolean;

  ngOnInit(): void {
    this.loadingAlreadyUploadedFiles = this.getUploadedFiles;
    if (this.getUploadedFiles) {
      this.indexDBService.getAll('authorities').subscribe((files) => {
        this.files = files;
        if (this.files.length > 0) {
          this.files = this.files.filter((file) => (file.type === this.authorityType));
        }
        this.loadingAlreadyUploadedFiles = false;
      });
    }
  }

  addFile(event: FileList): void {
    // When appending, the already selected files count towards the maximum size as well
    const alreadySelectedFileSize = this.appendFiles ? this.getSelectedFileSize() : 0;
    const verifyFiles = this.isValidFileSize(event, alreadySelectedFileSize);
    const filesToPush = [];
    // if the files did not pass the verification the result is true and a message contains the problem
    if (verifyFiles.result) {
      this.errorMessage = verifyFiles.message;
      return;
    }

    for (let index = 0; index < event.length; index++) {
      const element = event[index];
      if (element.size <= 0) {
        this.errorMessage = "Ihre Datei ist zu klein. Das kann vorkommen, wenn Sie die Datei zum Beispiel aus einem ZIP Ordner hochladen ohne diesen vorher zu entpacken."
        return;
      }
      if (event[index].size > this.maxFileSize) {
        this.errorMessage = `Die Datei darf nicht größer als ${this.maxFileSize/1000000} MB sein.`;
        return;
      }
      if (element.type != "application/pdf") {
        this.errorMessage = "Bitte laden Sie nur .pdf Dateien hoch."
        return;
      }

      filesToPush.push({file: element, fileName: element.name, authorityId: null});
      this.errorMessage = "";
    }

    if (this.appendFiles) {
      this.files = [...this.files, ...filesToPush];
      // Reset the input so a previously removed file can be selected again
      if (this.fileInput) {
        this.fileInput.nativeElement.value = null;
      }
    } else {
      this.files = [...filesToPush];
    }
  }

  /**
   * Verifies if the files are valid (filesize bigger than 0 bytes) and together not more than the maximum file size (maxFileSize)
   * @param files The passed files to verify
   * @param alreadySelectedFileSize Size in bytes of the files that are already selected and count towards the maximum size
   */
  isValidFileSize(files: FileList, alreadySelectedFileSize: number = 0): { result: boolean, message: string } {
    let cumulatedFileSize = alreadySelectedFileSize;
    for (let index = 0; index < files.length; index++) {
      const element = files[index];

      if (element.size <= 0) {
        return {result: true, message: "Ihre Datei ist zu klein. Das kann vorkommen, wenn Sie die Datei zum Beispiel aus einem ZIP Ordner hochladen ohne diesen vorher zu entpacken."};
      } else {
        cumulatedFileSize+= element.size;
      }
    }

    return {result: cumulatedFileSize >= this.maxFileSize, message: `Die maximale Größe der Datei(en) darf ${this.maxFileSize/1000000} MB nicht überschreiten.`};
  }

  /**
   * Uploads the files within the dropzone and returns a Promise<boolean> true when everything was uploaded successfully or else false
   */
  uploadFilesPromise(authorityType: string): Promise<boolean> {
    const promises = this.files.map(async file => {
      if (!file.authorityId) {
        this.authoritySevice.saveFile(file.file, this.authoritySevice.getLastPropertyHash(), authorityType);
      }
    });

    return Promise.all(promises)
        .then(() => true)
        .catch(error => {
          Sentry.captureException(error);
          console.log(error);
          return false;
        });
  }

  /**
   * Removes the selected file from the dropzones file list
   * The http-requests currently always returns an error since there is no check if the file was uploaded before trying to delete it within the backend
   * @param index The index of the file within the list
   */
  deleteAttachment(index) {
    if (this.files[index].authorityId) {
      this.deleteFile(this.files[index].authorityId);
    } else {
      this.files.splice(index, 1);
    }
  }

  deleteFile(fileKey: number): void {
    this.indexDBService.deleteByKey('authorities', fileKey).subscribe(() => {
      this.files = this.files.filter((file) => file.authorityId !== fileKey);
    });
  }

  /**
   * Returns the cumulated size in bytes of all currently selected files
   */
  private getSelectedFileSize(): number {
    return this.files.reduce((size: number, file: any) => size + (file.file?.size ?? 0), 0);
  }

}
